import { useEffect, useRef, useState } from 'react';
import AppModal from '../components/AppModal';
import { apiUrl } from '../config/appConfig';
import api from '../services/api';
import { obtenerUsuarioSesion } from '../utils/sesion';

function estadoInicial() {
  return {
    Id: null,
    Nombre: '',
    RazonSocial: '',
    Prefijo: '',
    Direccion: '',
    Correo: '',
    LogoPath: '',
    ColorLineas: '#000000',
    MostrarLogo: true,
    MostrarPiePagina: true,
    RevisionFormato: 'Rev007'
  };
}

function urlLogo(nombreArchivo) {
  if (!nombreArchivo) return '';

  return apiUrl(`/empresas/logo/${nombreArchivo}`);
}

function EmpresasPage() {
  const [empresas, setEmpresas] = useState([]);
  const [form, setForm] = useState(estadoInicial());
  const [mensaje, setMensaje] = useState('');
  const [errores, setErrores] = useState([]);
  const [editando, setEditando] = useState(false);
  const [logoArchivo, setLogoArchivo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [empresaEstatusModal, setEmpresaEstatusModal] = useState(null);

  const usuarioActual = obtenerUsuarioSesion();
  const esAdministrador = usuarioActual.Rol === 'Administrador';

  const inputLogoRef = useRef(null);

  useEffect(() => {
    cargarEmpresas();
  }, []);

  async function cargarEmpresas() {
    try {
      const res = await api.get('/empresas');
      setEmpresas(res.data);
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }

  function seleccionarLogo(e) {
    const archivo = e.target.files[0];

    if (!archivo) return;

    setLogoArchivo(archivo);
    setLogoPreview(URL.createObjectURL(archivo));

    setForm(prev => ({
      ...prev,
      LogoPath: archivo.name
    }));

    e.target.value = '';
  }

  function validar() {
    const pendientes = [];

    if (!form.Nombre.trim()) {
      pendientes.push('Nombre es obligatorio.');
    }

    if (!form.Prefijo.trim()) {
      pendientes.push('Prefijo es obligatorio.');
    }

    if (!form.ColorLineas.trim()) {
      pendientes.push('Color de línea es obligatorio.');
    }

    if (!form.RevisionFormato.trim()) {
      pendientes.push('Revisión es obligatoria.');
    }

    return pendientes;
  }

  function limpiarFormulario() {
    setForm(estadoInicial());
    setEditando(false);
    setMensaje('');
    setErrores([]);
    setLogoArchivo(null);
    setLogoPreview('');
  }

  async function guardar(e) {
    e.preventDefault();

    setMensaje('');
    setErrores([]);

    const pendientes = validar();

    if (pendientes.length > 0) {
      setErrores(pendientes);
      setMensaje('Revisa los campos antes de guardar.');
      return;
    }

    try {
      let empresaId = form.Id;

      if (editando) {
        await api.put(`/empresas/${form.Id}`, form);
        empresaId = form.Id;
      } else {
        const res = await api.post('/empresas', form);
        empresaId = res.data.data.Id;
      }

      if (logoArchivo && empresaId) {
        const formData = new FormData();
        formData.append('logo', logoArchivo);

        await api.post(`/empresas/${empresaId}/logo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      setMensaje(
        editando
          ? 'Empresa actualizada correctamente.'
          : 'Empresa creada correctamente.'
      );

      limpiarFormulario();
      await cargarEmpresas();
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function editarEmpresa(empresa) {
    setForm({
      Id: empresa.Id,
      Nombre: empresa.Nombre || '',
      RazonSocial: empresa.RazonSocial || '',
      Prefijo: empresa.Prefijo || '',
      Direccion: empresa.Direccion || '',
      Correo: empresa.Correo || '',
      LogoPath: empresa.LogoPath || '',
      ColorLineas: empresa.ColorLineas || '#000000',
      MostrarLogo: Boolean(empresa.MostrarLogo),
      MostrarPiePagina: Boolean(empresa.MostrarPiePagina),
      RevisionFormato: empresa.RevisionFormato || 'Rev007'
    });

    setLogoArchivo(null);

    if (empresa.LogoPath) {
      setLogoPreview(urlLogo(empresa.LogoPath));
    } else {
      setLogoPreview('');
    }

    setEditando(true);
    setMensaje('');
    setErrores([]);

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function cambiarEstatusEmpresa(empresa) {
    setEmpresaEstatusModal(empresa);
  }

  async function confirmarCambioEstatusEmpresa() {
    const empresa = empresaEstatusModal;

    if (!empresa) return;

    setEmpresaEstatusModal(null);

    try {
      await api.delete(`/empresas/${empresa.Id}`);
      setMensaje(
        empresa.Activo
          ? 'Empresa desactivada correctamente.'
          : 'Empresa activada correctamente.'
      );
      await cargarEmpresas();
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  return (
    <div className="page-container-normal">

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-1">Empresas</h1>

          <div className="text-muted">
            Administración de empresas, identidad visual y formato PDF
          </div>
        </div>
      </div>

      {mensaje && (
        <div className="alert alert-info">
          {mensaje}
        </div>
      )}

      {errores.length > 0 && (
        <div className="alert alert-warning">
          <div className="fw-bold mb-2">
            ⚠ Campos pendientes:
          </div>

          <ul className="mb-0">
            {errores.map((error, index) => (
              <li key={index}>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <input
        ref={inputLogoRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        onChange={seleccionarLogo}
        style={{ display: 'none' }}
      />

      <form onSubmit={guardar}>
        <div className="card-box mb-3">

          <div className="row g-4 align-items-start">

            <div className="col-lg-9">

              <div className="row g-3">

                <div className="col-md-4">
                  <label className="form-label">
                    Nombre <span className="text-danger">*</span>
                  </label>

                  <input
                    className="form-control"
                    name="Nombre"
                    value={form.Nombre}
                    onChange={handleChange}
                    placeholder="Nombre corto"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Razón Social
                  </label>

                  <input
                    className="form-control"
                    name="RazonSocial"
                    value={form.RazonSocial}
                    onChange={handleChange}
                    placeholder="Razón social completa"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Prefijo folio <span className="text-danger">*</span>
                  </label>

                  <input
                    className="form-control"
                    name="Prefijo"
                    value={form.Prefijo}
                    onChange={handleChange}
                    placeholder="MET"
                    maxLength="10"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Dirección
                  </label>

                  <input
                    className="form-control"
                    name="Direccion"
                    value={form.Direccion}
                    onChange={handleChange}
                    placeholder="Dirección fiscal o comercial"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Correo
                  </label>

                  <input
                    className="form-control"
                    name="Correo"
                    value={form.Correo}
                    onChange={handleChange}
                    placeholder="correo@empresa.com"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Revisión <span className="text-danger">*</span>
                  </label>

                  <input
                    className="form-control"
                    name="RevisionFormato"
                    value={form.RevisionFormato}
                    onChange={handleChange}
                    placeholder="Rev007"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Ruta logo
                  </label>

                  <div className="input-group">
                    <input
                      className="form-control"
                      name="LogoPath"
                      value={form.LogoPath}
                      readOnly
                      placeholder="Selecciona logo"
                    />

                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      title="Seleccionar logo"
                      onClick={() => inputLogoRef.current?.click()}
                    >
                      📁
                    </button>
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Color línea <span className="text-danger">*</span>
                  </label>

                  <div className="d-flex gap-2">
                    <input
                      type="color"
                      className="form-control form-control-color"
                      name="ColorLineas"
                      value={form.ColorLineas || '#000000'}
                      onChange={handleChange}
                      style={{
                        width: '46px',
                        padding: '4px'
                      }}
                    />

                    <input
                      className="form-control"
                      name="ColorLineas"
                      value={form.ColorLineas}
                      onChange={handleChange}
                      placeholder="#000000"
                    />
                  </div>
                </div>

                <div className="col-md-4 d-flex align-items-end gap-4">

                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="MostrarLogo"
                      checked={form.MostrarLogo}
                      onChange={handleChange}
                      id="MostrarLogo"
                    />

                    <label className="form-check-label" htmlFor="MostrarLogo">
                      Logo
                    </label>
                  </div>

                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="MostrarPiePagina"
                      checked={form.MostrarPiePagina}
                      onChange={handleChange}
                      id="MostrarPiePagina"
                    />

                    <label className="form-check-label" htmlFor="MostrarPiePagina">
                      Pie
                    </label>
                  </div>

                </div>

              </div>

            </div>

            <div className="col-lg-3">

              <label className="form-label">
                Logo
              </label>

              <div
                style={{
                  width: '200px',
                  height: '200px',
                  maxWidth: '100%',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '14px',
                  background: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  margin: '0 auto'
                }}
              >
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo empresa"
                    style={{
                      maxWidth: '180px',
                      maxHeight: '180px',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <span
                    className="text-muted"
                    style={{
                      fontSize: '13px'
                    }}
                  >
                    Sin logo
                  </span>
                )}
              </div>

              <div className="d-flex justify-content-center gap-2 mt-3">
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                >
                  {editando ? 'Guardar cambios' : 'Crear empresa'}
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={limpiarFormulario}
                >
                  {editando ? 'Cancelar' : 'Limpiar'}
                </button>
              </div>

            </div>

          </div>

        </div>
      </form>

      <div className="card-box">

        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">
            Empresas registradas
          </h5>

          <span className="badge bg-secondary">
            {empresas.length} registros
          </span>
        </div>

        <div className="table-wrap">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Prefijo</th>
                <th>Logo</th>
                <th>Color línea</th>
                <th className="text-center">Mostrar logo</th>
                <th className="text-center">Pie</th>
                <th>Revisión</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {empresas.map(empresa => (
                <tr key={empresa.Id}>

                  <td>
                    <div className="fw-bold">
                      {empresa.Nombre}
                    </div>

                    {empresa.RazonSocial && (
                      <div className="text-muted">
                        {empresa.RazonSocial}
                      </div>
                    )}
                  </td>

                  <td>
                    {empresa.Prefijo}
                  </td>

                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {empresa.LogoPath ? (
                        <>
                          <img
                            src={urlLogo(empresa.LogoPath)}
                            alt="Logo"
                            style={{
                              width: '34px',
                              height: '34px',
                              objectFit: 'contain',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              background: '#fff'
                            }}
                          />

                          <span className="text-muted">
                            {empresa.LogoPath}
                          </span>
                        </>
                      ) : (
                        <span className="badge bg-secondary">
                          Sin logo
                        </span>
                      )}
                    </div>
                  </td>

                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '14px',
                        height: '14px',
                        borderRadius: '4px',
                        background: empresa.ColorLineas || '#000000',
                        border: '1px solid #ccc',
                        marginRight: '6px',
                        verticalAlign: 'middle'
                      }}
                    />
                    {empresa.ColorLineas || '#000000'}
                  </td>

                  <td className="text-center">
                    {empresa.MostrarLogo ? (
                      <span className="badge bg-success">
                        Sí
                      </span>
                    ) : (
                      <span className="badge bg-secondary">
                        No
                      </span>
                    )}
                  </td>

                  <td className="text-center">
                    {empresa.MostrarPiePagina ? (
                      <span className="badge bg-success">
                        Sí
                      </span>
                    ) : (
                      <span className="badge bg-secondary">
                        No
                      </span>
                    )}
                  </td>

                  <td>
                    {empresa.RevisionFormato || 'Rev007'}
                  </td>

                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        onClick={() => editarEmpresa(empresa)}
                      >
                        Editar
                      </button>

                      {esAdministrador && (
                        <button
                          className={
                            empresa.Activo
                              ? 'btn btn-outline-danger'
                              : 'btn btn-outline-success'
                          }
                          type="button"
                          onClick={() => cambiarEstatusEmpresa(empresa)}
                        >
                          {empresa.Activo ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                    </div>
                  </td>

                </tr>
              ))}

              {empresas.length === 0 && (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center text-muted py-4"
                  >
                    No hay empresas registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      <AppModal
        abierto={Boolean(empresaEstatusModal)}
        titulo="Sistema Recibos"
        mensaje={
          empresaEstatusModal
            ? `¿Deseas ${empresaEstatusModal.Activo ? 'desactivar' : 'activar'} esta empresa?`
            : ''
        }
        variante={empresaEstatusModal?.Activo ? 'danger' : 'warning'}
        textoCancelar="Cancelar"
        textoAceptar={empresaEstatusModal?.Activo ? 'Desactivar' : 'Activar'}
        onCancelar={() => setEmpresaEstatusModal(null)}
        onAceptar={confirmarCambioEstatusEmpresa}
      />

    </div>
  );
}

export default EmpresasPage;
